/**
 * Color validation utilities using regex patterns
 */

// Regex building blocks for color value validation
const RGB_CHANNEL = `(?:25[0-5]|2[0-4][0-9]|1[0-9]{2}|[1-9]?[0-9])`; // 0-255
const PERCENT = `(?:100|[1-9]?[0-9])%`; // 0-100%
const RGB_VALUE = `(?:${RGB_CHANNEL}|${PERCENT})`; // 0-255 or 0-100%
const ALPHA = `(?:(?:0(?:\\.[0-9]+)?|1(?:\\.0+)?)|${PERCENT})`; // 0-1 or 0-100%
const HUE = `(?:360|3[0-5][0-9]|[12][0-9]{2}|[1-9]?[0-9])(?:deg|rad|grad|turn)?`; // 0-360
const SLV = `(?:100|[1-9]?[0-9])%?`; // 0-100 for saturation/lightness/value
const CMYK_CHANNEL = `(?:100|[1-9]?[0-9])%?`; // 0-100 for CMYK
const S = `\\s*`; // Whitespace

const colorPatterns = [
  // Hex: #RGB, #RRGGBB, #RGBA, #RRGGBBAA
  /^#(?:[0-9a-f]{3}|[0-9a-f]{4}|[0-9a-f]{6}|[0-9a-f]{8})$/i,
  // RGB/RGBA: rgb(r, g, b) or rgba(r, g, b, a) - alpha optional
  new RegExp(
    `^rgba?\\(${S}${RGB_VALUE}${S},${S}${RGB_VALUE}${S},${S}${RGB_VALUE}(?:${S},${S}${ALPHA})?${S}\\)$`,
    "i"
  ),
  // HSL/HSLA: hsl(h, s, l) or hsla(h, s, l, a) - alpha optional
  new RegExp(
    `^hsla?\\(${S}${HUE}${S},${S}${SLV}${S},${S}${SLV}(?:${S},${S}${ALPHA})?${S}\\)$`,
    "i"
  ),
  // HSV/HSVA: hsv(h, s, v) or hsva(h, s, v, a) - alpha optional
  new RegExp(
    `^hsva?\\(${S}${HUE}${S},${S}${SLV}${S},${S}${SLV}(?:${S},${S}${ALPHA})?${S}\\)$`,
    "i"
  ),
  // HSB/HSBA: hsb(h, s, b) or hsba(h, s, b, a) - alpha optional
  new RegExp(
    `^hsba?\\(${S}${HUE}${S},${S}${SLV}${S},${S}${SLV}(?:${S},${S}${ALPHA})?${S}\\)$`,
    "i"
  ),
  // CMYK: cmyk(c, m, y, k)
  new RegExp(
    `^cmyk\\(${S}${CMYK_CHANNEL}${S},${S}${CMYK_CHANNEL}${S},${S}${CMYK_CHANNEL}${S},${S}${CMYK_CHANNEL}${S}\\)$`,
    "i"
  ),
];

/**
 * Check if a string is ONLY a valid color value with in-range values.
 * Rejects out-of-range values like rgba(999,999,999,0.1).
 *
 * @example
 * isOnlyColorValue("#FF0000") // true
 * isOnlyColorValue("rgba(255, 0, 0, 1)") // true
 * isOnlyColorValue("rgba(999, 0, 0, 1)") // false - R value out of range
 * isOnlyColorValue("The color is #FF0000") // false - not only a color
 */
export const isOnlyColorValue = (str: string): boolean => {
  return colorPatterns.some(pattern => pattern.test(str.trim()));
};
